import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../../constants/endpoints';

export interface UserSuggestion {
  _id: string;
  name: string;
  email: string;
  profilePictureUrl: string | null;
  mutualConnections: number;
  hasRequestPending?: boolean;
  isLookingForJob?:string;

   aboutMe?:string
}

export interface UserSearchResult {
  _id: string;
  name: string;
  email: string;
  whatsappNumber: string;
  profilePictureUrl: string | null;
  isFollowing: boolean;
  hasRequestPending: boolean;
  aboutMe?:string;
  isLookingForJob?:string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface SuggestionsResponse {
  success: boolean;
  data: {
    suggestions: UserSuggestion[];
    pagination: Pagination;
  };
}

export interface SearchResponse {
  success: boolean;
  data: {
    users: UserSearchResult[];
    pagination: Pagination;
  };
}

export interface SuggestionsParams {
  page?: number;
  limit?: number;
  workMode?: string;
  noticeType?: string;
  jobType?: string;
  experienceLevel?: string;
  location?: string;
}

export interface SearchParams {
  query: string;
  page?: number;
  limit?: number;
  workMode?: string;
  noticeType?: string;
  jobType?: string;
  experienceLevel?: string;
  location?: string;
}

export interface FollowResponse {
  success: boolean;
  message: string;
}

export interface FollowRequest {
  recipientId: string;
}

export interface FollowRequestItem {
  _id: string;
  requester: {
    _id: string;
    email: string;
    name?: string;
    profilePictureUrl?: string;
    aboutMe?:string;
    hasRequestPending?: boolean;
  };
  recipient: string | {
    _id: string;
    email: string;
    name?: string;
    profilePictureUrl?: string;
    aboutMe?:string;
    hasRequestPending?: boolean;
  };
  status: 'pending' | 'accepted' | 'rejected';
  requestedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface FollowRequestsResponse {
  success: boolean;
  data: {
    requests: FollowRequestItem[];
    pagination: Pagination;
  };
}

export interface FollowRequestsParams {
  type: 'received' | 'sent';
  page?: number;
  limit?: number;
}

export interface RespondToRequestData {
  response: 'accepted' | 'rejected';
}

export interface RespondToRequestResponse {
  success: boolean;
  message: string;
}

export interface FollowerFollowingUser {
  _id: string;
  name: string;
  email: string;
  aboutMe?:string
  profilePictureUrl: string | null;
  status: 'followers' | 'following';
  isRequestPending?: boolean;
}

export interface FollowersResponse {
  success: boolean;
  data: {
    followers: FollowerFollowingUser[];
    pagination: Pagination;
  };
}

export interface FollowingResponse {
  success: boolean;
  data: {
    following: FollowerFollowingUser[];
    pagination: Pagination;
  };
}

export interface FollowersFollowingParams {
  page?: number;
  limit?: number;
}

export const followService = {
  getSuggestions: async (params: SuggestionsParams = {}): Promise<SuggestionsResponse> => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.workMode) queryParams.append('workMode', params.workMode);
    if (params.noticeType) queryParams.append('noticeType', params.noticeType);
    if (params.jobType) queryParams.append('jobType', params.jobType);
    if (params.experienceLevel) queryParams.append('experienceLevel', params.experienceLevel);
    if (params.location) queryParams.append('location', params.location);

    const response = await apiClient.get<SuggestionsResponse>(
      `${API_ENDPOINTS.FOLLOW.SUGGESTIONS}?${queryParams.toString()}`
    );
    return response;
  },

  searchUsers: async (params: SearchParams): Promise<SearchResponse> => {
    const queryParams = new URLSearchParams();
    queryParams.append('query', params.query);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.workMode) queryParams.append('workMode', params.workMode);
    if (params.noticeType) queryParams.append('noticeType', params.noticeType);
    if (params.jobType) queryParams.append('jobType', params.jobType);
    if (params.experienceLevel) queryParams.append('experienceLevel', params.experienceLevel);
    if (params.location) queryParams.append('location', params.location);

    const response = await apiClient.get<SearchResponse>(
      `${API_ENDPOINTS.FOLLOW.SEARCH}?${queryParams.toString()}`
    );
    return response;
  },

  followUser: async (data: FollowRequest): Promise<FollowResponse> => {
    const response = await apiClient.post<FollowResponse>(
      API_ENDPOINTS.FOLLOW.REQUEST,
      data
    );
    return response;
  },

  unfollowUser: async (userId: string): Promise<FollowResponse> => {
    const response = await apiClient.delete<FollowResponse>(
      `${API_ENDPOINTS.FOLLOW.UNFOLLOW}/${userId}`
    );
    return response;
  },

  cancelFollowRequest: async (data: FollowRequest): Promise<FollowResponse> => {
    const response = await apiClient.delete<FollowResponse>(
      API_ENDPOINTS.FOLLOW.CANCEL_REQUEST,
      { data }
    );
    return response;
  },

  getFollowRequests: async (params: FollowRequestsParams): Promise<FollowRequestsResponse> => {
    const queryParams = new URLSearchParams();
    queryParams.append('type', params.type);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const response = await apiClient.get<FollowRequestsResponse>(
      `${API_ENDPOINTS.FOLLOW.REQUESTS}?${queryParams.toString()}`
    );
    return response;
  },

  respondToRequest: async (requestId: string, data: RespondToRequestData): Promise<RespondToRequestResponse> => {
    const response = await apiClient.put<RespondToRequestResponse>(
      API_ENDPOINTS.FOLLOW.RESPOND(requestId),
      data
    );
    return response;
  },

  getFollowers: async (userId: string, params: FollowersFollowingParams = {}): Promise<FollowersResponse> => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const response = await apiClient.get<FollowersResponse>(
      `${API_ENDPOINTS.FOLLOW.FOLLOWERS(userId)}?${queryParams.toString()}`
    );
    return response;
  },

  getFollowing: async (userId: string, params: FollowersFollowingParams = {}): Promise<FollowingResponse> => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const response = await apiClient.get<FollowingResponse>(
      `${API_ENDPOINTS.FOLLOW.FOLLOWING(userId)}?${queryParams.toString()}`
    );
    return response;
  },
};